// Simple session management untuk Tim Produksi
const SESSION_KEY = "produksi_team_session";

export const normalizeMember = (member) => {
  if (!member) return null;
  const m = { ...member };
  if (typeof m.page_access === 'string') {
    try {
      m.page_access = JSON.parse(m.page_access || '{}');
    } catch {
      m.page_access = {};
    }
  }
  if (typeof m.notification_preferences === 'string') {
    try {
      m.notification_preferences = JSON.parse(m.notification_preferences || '{}');
    } catch {
      m.notification_preferences = {};
    }
  }
  return m;
};

export const setSession = (member) => {
  try {
    const normalized = normalizeMember(member);
    localStorage.setItem(SESSION_KEY, JSON.stringify(normalized));
  } catch {}
};

export const getSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? normalizeMember(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

export const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem('companySettings');
  } catch {}
};

export const isLoggedIn = () => !!getSession();

// Hierarchy: developer(6) > owner(5) > admin(4) > supervisor_office(3.5) > supervisor(3) > operator_office(2) > operator_produksi(1)
const ROLE_LEVEL = {
  developer: 6,
  owner: 5,
  admin: 4,
  kepala: 3,
  staff: 1,
};

// Urutan tampilan di Tim Produksi (organigram)
// owner → developer → admin → spv office → spv produksi → op. office → op. produksi
export const ROLE_ORDER = [
  "owner",
  "developer",
  "admin",
  "kepala",
  "staff",
];

export const hasRole = (member, minRole) =>
  (ROLE_LEVEL[member?.role] || 0) >= (ROLE_LEVEL[minRole] || 0);

export const checkIsDev = (m) => 
  m?.role === "developer" || 
  (m?.department && m.department.toLowerCase().includes('developer')) || 
  (m?.position && m.position.toLowerCase().includes('developer'));
  
export const checkIsOwner = (m) => 
  m?.role === "owner" || 
  (m?.department && m.department.toLowerCase().includes('direktur')) ||
  (m?.position && m.position.toLowerCase().includes('owner'));

// Developer hanya bisa dilihat namanya oleh owner & admin ke atas
export const canViewDeveloper = (currentMember) =>
  checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember?.role === "admin";

export const canViewSensitiveData = (currentMember, targetMember) => {
  if (checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember?.role === "admin") return true;
  if (["supervisor", "supervisor_office"].includes(currentMember?.role) &&
      ["operator_office", "operator_produksi"].includes(targetMember?.role)) return true;
  return currentMember?.id === targetMember?.id;
};

// ── RBAC Policies ────────────────────────────────────────────────

// Aliases / Fallbacks for backward compatibility
const KEY_ALIASES = {
  'po_produksi': ['produksi'],
  'produksi': ['po_produksi'],
  'absensi': ['manajemen_tim'],
  'manajemen_tim': ['absensi'],
  'pengaturan_akses': ['pengaturan'],
  'log_aktivitas': ['pengaturan'],
  'pengaturan_perusahaan': ['pengaturan'],
  'pengaturan': ['pengaturan_perusahaan', 'pengaturan_akses'],
  'simulasi_biaya': ['semua_akses'],
  'batch_database': ['semua_akses'],
  'dashboard_workspace': ['dashboard'],
  'dashboard': ['dashboard_workspace', 'dashboard_analytics'],
  'dashboard_analytics': ['dashboard'],
  'finance_sph_list': ['finance_calculator'],
  'finance_custom_invoices': ['invoice_po'],
  'finance_payables': ['finance_payables_receivables'],
  'finance_receivables': ['finance_payables_receivables'],
  'finance_reports_hub': ['finance_laporan'],
  'marketing_sph_list': ['marketing_calculator'],
  'marketing_custom_invoices': ['marketing_invoices'],
  'marketing_inventory': ['stok_inventori'],
  'purchasing_items_to_buy': ['barang_harus_dibeli'],
  'purchasing_items_po': ['barang_harus_dibeli'],
  'purchasing_data_pemesan': ['database_supplier'],
  'purchasing_inventory': ['stok_inventori'],
  'purchasing_client_data': ['data_klien'],
  'purchasing_delivery': ['delivery'],
};

/**
 * Dapatkan tingkat akses member untuk halaman tertentu.
 * Mengutamakan member.page_access, fallback ke logika role lama.
 */
export const getPageAccess = (member, pageKey) => {
  if (!member) return "none";
  
  // Developer always has full access
  if (checkIsDev(member)) return "edit";
  
  let pa = member.page_access;
  if (typeof pa === 'string') {
    try {
      pa = JSON.parse(pa || '{}');
    } catch {
      pa = {};
    }
  }

  // Strict page_access check
  if (pa && pa[pageKey] !== undefined) {
    return pa[pageKey];
  }

  // Fallback to alias if present
  if (KEY_ALIASES[pageKey]) {
    for (const alias of KEY_ALIASES[pageKey]) {
      if (pa && pa[alias] !== undefined) {
        return pa[alias];
      }
    }
  }

  // Owner default fallback full access if not defined in page_access
  if (checkIsOwner(member)) return "edit";

  // Fallback if no page_access configured at all
  if (!pa || Object.keys(pa).length === 0) {
    const r = (member.role || '').toLowerCase();
    const d = (member.department || '').toLowerCase();
    if (pageKey === 'portal_production' && (r.includes('produksi') || d.includes('produksi'))) return 'edit';
    if (pageKey === 'portal_finance' && (r.includes('finance') || d.includes('finance'))) return 'edit';
    if (pageKey === 'portal_marketing' && (r.includes('marketing') || d.includes('marketing'))) return 'edit';
    if (pageKey === 'portal_purchasing' && (r.includes('purchasing') || d.includes('purchasing'))) return 'edit';
    if (pageKey === 'portal_workspace' && (r.includes('owner') || r.includes('developer') || r.includes('admin'))) return 'edit';
  }

  // Jika pageKey berupa portal, tapi tidak ada di page_access
  return "none";
};

export const getShowPriceAccess = (member, pageKey) => {
  if (!member) return false;
  if (checkIsDev(member)) return true;
  
  let pa = member.page_access;
  if (typeof pa === 'string') {
    try {
      pa = JSON.parse(pa || '{}');
    } catch {
      pa = {};
    }
  }
  
  if (pa && pa[`${pageKey}_show_price`] !== undefined) {
    return pa[`${pageKey}_show_price`];
  }

  // Fallback to alias if present
  if (KEY_ALIASES[pageKey]) {
    for (const alias of KEY_ALIASES[pageKey]) {
      if (pa && pa[`${alias}_show_price`] !== undefined) {
        return pa[`${alias}_show_price`];
      }
    }
  }
  
  if (checkIsOwner(member)) return true;
  
  return false;
};

/** Bisa lihat QR milik orang lain? developer/owner/admin = semua, lainnya = hanya diri sendiri */
export const canViewOtherQR = (currentMember) =>
  checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember?.role === "admin";

/** Bisa menambah/edit anggota tim? Menggunakan getPageAccess */
export const canManageTeam = (currentMember) =>
  getPageAccess(currentMember, "manajemen_tim") === "edit";

/** Bisa edit Pengaturan Perusahaan? Menggunakan getPageAccess */
export const canEditCompanySettings = (currentMember) =>
  getPageAccess(currentMember, "pengaturan") === "edit";

/** Bisa edit Riwayat HPP (Input Bayar, Edit record)? Menggunakan getPageAccess */
export const canEditRiwayatHPP = (currentMember) =>
  getPageAccess(currentMember, "riwayat_hpp") === "edit";

/** Bisa akses laporan? Menggunakan getPageAccess */
export const canEditLaporan = (currentMember) =>
  getPageAccess(currentMember, "laporan") === "edit";

/** Bisa kelola pengiriman (barang masuk & keluar)? Menggunakan getPageAccess */
export const canManageDelivery = (currentMember) =>
  getPageAccess(currentMember, "delivery") === "edit";

/** Bisa kelola orderan selesai (Shipped)? Menggunakan getPageAccess */
export const canManageSelesai = (currentMember) =>
  getPageAccess(currentMember, "selesai") === "edit";

/** Bisa kelola pengembalian (Return)? Menggunakan getPageAccess */
export const canManageReturn = (currentMember) =>
  getPageAccess(currentMember, "return") === "edit";

/** Bisa tambah Rekening Bank? Meminjam hak riwayat HPP */
export const canManageBank = (currentMember) =>
  getPageAccess(currentMember, "riwayat_hpp") === "edit";

/** Bisa tambah/edit PO (Production Order)? Menggunakan getPageAccess produksi atau hak Kepala/Leader Produksi & Pengemasan */
export const canManagePO = (currentMember) => {
  if (!currentMember) return false;
  if (checkIsDev(currentMember) || checkIsOwner(currentMember)) return true;
  const r = (currentMember.role || '').toLowerCase().trim();
  const p = (currentMember.position || '').toLowerCase().trim();
  const d = (currentMember.division || currentMember.divisi || currentMember.department || '').toLowerCase().trim();
  
  const allowedRoles = [
    'developer', 'owner', 'direktur', 'admin', 
    'kepala_produksi', 'kepala_produksi_primer', 'kepala_produksi_sekunder', 'kepala_pengemasan',
    'qc', 'quality_control', 'kepala_qc', 'staff_qc', 'supervisor_qc', 'leader_qc'
  ];
  if (allowedRoles.includes(r.replace(/\s+/g, '_')) || allowedRoles.includes(p.replace(/\s+/g, '_'))) return true;
  if (r.includes('qc') || p.includes('qc') || d.includes('qc')) return true;
  if ((r.includes('kepala') || p.includes('kepala') || r.includes('supervisor') || p.includes('supervisor') || r.includes('leader')) && 
      (d.includes('produksi') || r.includes('produksi') || r.includes('pengemasan') || p.includes('pengemasan') || String(currentMember.tipe_produksi || '').toLowerCase().includes('sekunder') || String(currentMember.tipe_produksi || '').toLowerCase().includes('primer'))) {
    return true;
  }

  return getPageAccess(currentMember, "po_produksi") === "edit" || getPageAccess(currentMember, "produksi") === "edit";
};

/** Bisa tambah/edit di halaman Log Produksi? Menggunakan getPageAccess dashboard */
export const canManageProduksi = (currentMember) => {
  if (!currentMember) return false;
  if (canManagePO(currentMember)) return true;
  return getPageAccess(currentMember, "dashboard") === "edit";
};

/** Bisa lihat halaman Kalkulator HPP? Menggunakan getPageAccess */
export const canAccessHPP = (currentMember) =>
  getPageAccess(currentMember, "kalkulator_hpp") !== "none";

/** Bisa lihat halaman Riwayat HPP? Menggunakan getPageAccess */
export const canAccessRiwayatHPP = (currentMember) =>
  getPageAccess(currentMember, "riwayat_hpp") !== "none";

/** Bisa lihat halaman Stok & Inventori? Menggunakan getPageAccess */
export const canAccessStok = (currentMember) =>
  getPageAccess(currentMember, "stok_inventori") !== "none";

/** Role hanya bisa lihat (view-only), tidak bisa edit/tambah apapun. Dimap ke fungsi lama */
export const isViewOnly = (currentMember) =>
  ["operator_office", "operator_produksi"].includes(currentMember?.role);

/** Supervisor produksi hanya bisa lihat di produksi & PO (tidak bisa tambah/edit) */
export const isSupervisorProduksiOnly = (currentMember) =>
  currentMember?.role === "supervisor";

/** Role yang TIDAK boleh dipilih saat assign/tambah/edit anggota tim. */
export const UNASSIGNABLE_ROLES = ["developer", "owner"];

/** Label display untuk setiap role */
export const ROLE_LABEL = {
  developer: "Developer",
  owner: "Direktur Utama",
  admin: "Admin",
  kepala: "Kepala",
  staff: "Staff",
};